import {
  getFontCss,
  Popup,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import moment, { Duration } from 'moment-timezone'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { match, matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import ConfirmationPopupContent from 'src/components/ConfirmationPopupContent/ConfirmationPopupContent'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import {
  OrgExpenseReportMatchParams,
  orgExpenseReportRouteProps,
} from 'src/navigation/routeProps'
import {
  usePostSendOrgExpReportReceiptReminder,
  usePostSendOrgReceiptReminder,
  useQueryOrgExpReportReceiptReminderStatus,
  useQueryOrgReceiptReminderStatus,
} from 'src/queries/recieptReminders.queries'
import { createSetIsSendReceiptReminderPopupOpen } from 'src/redux/actions/app.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectExpenseReportIdFromRoute } from 'src/redux/selectors/data/orgExpenseReports.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

const SendReceiptReminderPopup: React.FC = (): React.ReactElement => {
  const translate = useTranslate()

  const dispatch = useDispatch()
  const [timeUntilNextReminder, setTimeUntilNextReminder] = useState<
    Duration | undefined
  >()

  const locale = useSelector(selectNullSafeLocaleFromState)
  const { analytics } = useAnalyticsContext()

  const location = useSelector(selectLocationFromState)
  const isExpReport = (
    matchPath<OrgExpenseReportMatchParams>(
      location?.pathname,
      orgExpenseReportRouteProps,
    ) as match<OrgExpenseReportMatchParams>
  )?.isExact
  const reportId = useSelector(selectExpenseReportIdFromRoute)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const id = isExpReport ? reportId : organization?.id || ''

  const {
    mutate: postSendReceiptReminder,
    isPending: postingReceiptReminder,
    error: postReceiptReminderError,
  } = isExpReport
    ? usePostSendOrgExpReportReceiptReminder()
    : usePostSendOrgReceiptReminder()

  const { data: reminderStatus, isPending: loading } = isExpReport
    ? useQueryOrgExpReportReceiptReminderStatus({ reportId: id })
    : useQueryOrgReceiptReminderStatus({ orgId: id })
  const remindersCount = reminderStatus?.usersMissingReceiptsCount
  const countdownIntervalId: ReturnType<typeof setInterval> | undefined =
    useMemo(() => {
      if (remindersCount !== undefined && remindersCount > 0) {
        const nextReceiptReminderTime = moment(
          reminderStatus?.lastReceiptReminderSentAt,
        ).add(1440, 'minutes')
        let timeDiff = nextReceiptReminderTime.diff(moment())

        if (timeDiff > 0) {
          setTimeUntilNextReminder(moment.duration(timeDiff))

          return setInterval((): void => {
            timeDiff = nextReceiptReminderTime.diff(moment())
            setTimeUntilNextReminder(moment.duration(timeDiff))

            if (timeDiff < 0) {
              clearInterval(countdownIntervalId)
            }
          }, 1000)
        }
      }
    }, [reminderStatus?.lastReceiptReminderSentAt])

  const subTitleTopText =
    remindersCount === 0
      ? translate(
          isExpReport
            ? "Looks like there's no one in violation of the receipt rule in this report! You don't need to send any receipt reminders at this time."
            : "Looks like there's no one in your org in violation of the receipt rule! You don't need to send any receipt reminders at this time.",
        )
      : translate(
          isExpReport
            ? remindersCount === 1
              ? 'You are about to send a receipt reminder to ${people} person missing a receipt for this report. Reminders for this report can only be sent once in a 24-hour period.'
              : 'You are about to send a receipt reminder to ${people} people missing a receipt for this report. Reminders for this report can only be sent once in a 24-hour period.'
            : remindersCount === 1
              ? 'There is ${people} person in your org who is missing receipts. Reminders can only be sent once in a 24-hour period.'
              : 'There are ${people} people in your org who are missing receipts. Reminders can only be sent once in a 24-hour period.',
          {
            people: (
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {remindersCount}
              </span>
            ),
          },
        )

  const subTitleBottomText =
    timeUntilNextReminder &&
    timeUntilNextReminder.asSeconds() > 0 &&
    translate('You can send your next receipt reminder in ${timeLeft}', {
      timeLeft: (
        <span
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
          `}
        >
          {timeUntilNextReminder.hours() > 0 ||
          timeUntilNextReminder.minutes() > 0
            ? `${timeUntilNextReminder.hours()} ${translateSingleString(locale, timeUntilNextReminder.hours() === 1 ? 'hour' : 'hours')}, ${timeUntilNextReminder.minutes()} ${translateSingleString(locale, timeUntilNextReminder.minutes() === 1 ? 'minute' : 'minutes')}`
            : `${timeUntilNextReminder.seconds()} ${translateSingleString(locale, timeUntilNextReminder.seconds() === 1 ? 'second' : 'seconds')}`}
        </span>
      ),
    })

  const handleTrackReceiptReminder = (reminderSent: boolean): void => {
    analytics?.track('Transactions - Receipt Reminder - button press', {
      reminder_type: 'bulk notification',
      reminder_sent: reminderSent,
    })
  }

  const closePopup = (): void => {
    dispatch(createSetIsSendReceiptReminderPopupOpen(false))
    if (
      remindersCount === 0 ||
      (timeUntilNextReminder && timeUntilNextReminder.asSeconds() > 0)
    ) {
      handleTrackReceiptReminder(false)
    }
  }

  useEffect(() => {
    return (): void => {
      clearInterval(countdownIntervalId)
    }
  }, [])

  return (
    <Popup
      onClickClose={closePopup}
      showCloseButton
      css={css`
        display: flex;
        position: relative;
      `}
    >
      {loading ? (
        <LoadingBackgroundPage />
      ) : (
        <ConfirmationPopupContent
          title={translate('Receipt Reminder')}
          subtitle={
            <>
              <p>{subTitleTopText}</p>
              {subTitleBottomText && (
                <p
                  css={css`
                    margin-top: 20px;
                  `}
                >
                  {subTitleBottomText}
                </p>
              )}
            </>
          }
          actionButtonLabel={translate('Send Receipt Reminder')}
          actionButtonOnClick={(): void => {
            postSendReceiptReminder({ id: id, body: {} })
            handleTrackReceiptReminder(true)
          }}
          actionButtonLoading={postingReceiptReminder}
          actionButtonVariant="primary"
          isCancelButtonLink
          cancelButtonLabel={translate('Cancel')}
          cancelButtonOnClick={closePopup}
          topLevelError={postReceiptReminderError?.error}
          actionButtonDisabled={
            remindersCount === 0 ||
            (timeUntilNextReminder && timeUntilNextReminder.asSeconds() > 0)
          }
        />
      )}
    </Popup>
  )
}

export default SendReceiptReminderPopup
